import { readJsonPref, writeJsonPref } from '@utils/localJsonPref';
import type { ColumnSort, SortDir } from './progressMatrix';

/**
 * Per-site memory of the Progress Tracker view settings (generation/level, sort,
 * direction), so leaving and returning to Activities restores them instead of
 * snapping back to Generation 1 / Default. Browser-local, keyed by siteId; the
 * store guarding lives in the shared localJsonPref helper. Every field is
 * validated on read so a stale/hand-edited value can't produce invalid state.
 */
const KEY_PREFIX = 'dorca.tracker.prefs.';

export interface ITrackerPrefs {
  generation: number;
  sort: ColumnSort;
  dir: SortDir;
  /** The legend's "hide 100%" toggle. */
  hideDone: boolean;
}

const VALID_SORTS: ReadonlySet<string> = new Set(['default', 'sequence', 'progress', 'ontime', 'critical']);

export function readTrackerPrefs(siteId: string): Partial<ITrackerPrefs> {
  if (!siteId) return {};
  const parsed = readJsonPref(KEY_PREFIX + siteId);
  if (!parsed || typeof parsed !== 'object') return {};
  const raw = parsed as Partial<ITrackerPrefs>;
  const out: Partial<ITrackerPrefs> = {};
  if (typeof raw.generation === 'number' && raw.generation >= 1) out.generation = Math.floor(raw.generation);
  if (typeof raw.sort === 'string' && VALID_SORTS.has(raw.sort)) out.sort = raw.sort as ColumnSort;
  if (raw.dir === 'asc' || raw.dir === 'desc') out.dir = raw.dir;
  if (typeof raw.hideDone === 'boolean') out.hideDone = raw.hideDone;
  return out;
}

export function writeTrackerPrefs(siteId: string, prefs: ITrackerPrefs): void {
  if (!siteId) return;
  writeJsonPref(KEY_PREFIX + siteId, prefs);
}
